import { useState } from "react";
import { Link } from "react-router-dom";
import { useNewsletterSubscribe } from "../lib/useNewsletterSubscribe";

/* ============================================================
   BOS newsletter form — full Operational Brief section
   Submits to /api/newsletter-subscribe.php (HostGator PHP endpoint).
   Brevo owns confirmation email, tokens, activation, unsubscribe.
   ============================================================ */

export const OperationalBriefSignup = ({
  eyebrow = "Monthly executive briefing",
  title = "The Operational Brief",
  strapline,
  copy = "Practical insights on Operational Clarity, leadership behaviour and reliable execution.",
}) => {
  const [imgOk, setImgOk] = useState(true);
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const { state, error, submit, reset } = useNewsletterSubscribe();

  const handleChange = (setter) => (e) => {
    if (state === "error") reset();
    setter(e.target.value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    await submit({ email, website });
  };

  const isSending = state === "sending";
  const isSuccess = state === "success";

  return (
    <section
      id="operational-brief-signup"
      data-testid="operational-brief-signup"
      className="relative bg-paper border-t border-rule overflow-hidden"
    >
      {imgOk && (
        <div
          aria-hidden="true"
          className="hidden lg:block absolute inset-y-0 left-0 w-[42%] bg-[#EFEBE2]"
        />
      )}

      <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-12 lg:gap-x-16 items-center">
          {imgOk && (
            <div className="lg:col-span-5 relative">
              <div className="relative w-full max-w-md mx-auto sm:max-w-lg lg:max-w-none lg:mx-0 lg:-ml-16 xl:-ml-24 before:absolute before:-left-3 before:-bottom-3 md:before:-left-5 md:before:-bottom-5 before:w-2/3 before:h-2/3 before:border-l-2 before:border-b-2 before:border-terra before:pointer-events-none">
                <img
                  src="/assets/operational-brief-mockup.webp"
                  srcSet="/assets/operational-brief-mockup.webp 1200w"
                  sizes="(max-width: 1024px) 90vw, 46vw"
                  width="1200"
                  height="817"
                  loading="lazy"
                  decoding="async"
                  alt="Cover and digital preview of The Operational Brief"
                  data-testid="brief-image"
                  className="relative block w-full aspect-[16/10] lg:aspect-[1200/817] object-cover object-[38%_45%] lg:object-[42%_center]"
                  onError={() => setImgOk(false)}
                />
              </div>
              <div
                aria-hidden="true"
                className="hidden lg:block absolute top-1/2 -right-2 xl:-right-6 w-16 xl:w-24 h-px bg-terra"
              />
            </div>
          )}

          <div className={imgOk ? "lg:col-span-7" : "lg:col-span-12 max-w-2xl"}>
            <p className="text-[12px] font-semibold uppercase tracking-[0.22em] text-terra">
              {eyebrow}
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-[2.4rem] leading-[1.1] tracking-tight max-w-xl">
              {title}
            </h2>
            {strapline && <p className="mt-3 font-display text-xl italic text-ink">{strapline}</p>}
            <p className="mt-5 max-w-xl text-base leading-relaxed text-mute">
              {copy}
            </p>

            {isSuccess ? (
              <div
                data-testid="brief-signup-success"
                role="status"
                aria-live="polite"
                className="mt-8 max-w-xl border-l-2 border-terra pl-6"
              >
                <h3 className="font-display text-2xl md:text-3xl tracking-tight">
                  Check your inbox
                </h3>
                <p className="mt-3 text-base leading-relaxed text-mute">
                  We have sent you an email to confirm your subscription to The Operational Brief.
                  Your subscription will become active after you select the confirmation link.
                </p>
              </div>
            ) : (
              <form
                data-testid="brief-signup-form"
                className="relative mt-8"
                onSubmit={onSubmit}
                noValidate
              >
                <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
                  <label htmlFor="brief-signup-email" className="sr-only">
                    Work email
                  </label>
                  <input
                    id="brief-signup-email"
                    data-testid="brief-signup-email"
                    type="email"
                    placeholder="Work email"
                    value={email}
                    onChange={handleChange(setEmail)}
                    disabled={isSending}
                    aria-invalid={state === "error"}
                    aria-describedby={state === "error" ? "brief-signup-error" : undefined}
                    className="flex-1 bg-transparent border-b border-ink/25 outline-none py-3 text-base placeholder:text-mute/50 disabled:opacity-50 disabled:cursor-not-allowed focus:border-ink transition-colors"
                  />
                  <button
                    type="submit"
                    data-testid="brief-signup-submit"
                    disabled={isSending}
                    className="inline-flex items-center justify-center gap-2 bg-ink text-paper text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-terra transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSending ? "Sending..." : "Subscribe"}
                  </button>
                </div>

                {/* Honeypot — visually and semantically hidden from real users */}
                <div aria-hidden="true" className="absolute -left-[9999px] w-px h-px overflow-hidden">
                  <label>
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      data-testid="brief-signup-website"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </label>
                </div>

                <p className="mt-5 max-w-xl text-sm leading-relaxed text-mute">
                  By subscribing, you’ll receive The Operational Brief and occasional relevant
                  updates. You can unsubscribe at any time.{" "}
                  <Link
                    to="/privacy-policy"
                    data-testid="brief-privacy-link"
                    className="link-rule underline underline-offset-2"
                  >
                    Privacy Policy
                  </Link>
                </p>

                {state === "error" && (
                  <p
                    id="brief-signup-error"
                    data-testid="brief-signup-error"
                    role="alert"
                    className="mt-4 text-sm font-medium text-terra"
                  >
                    {error}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
